<template>
    <div class="main-layout__container">
        <div class="content__header">
            <div class="content-header__title">Авто</div>

            <div class="content-header__actions">
                <router-link v-if="canAddCar()" :to="{ name: 'AddCar' }">
                    <UISimpleButton>
                        <template #icon>
                            <img v-svg-inline src="@/assets/icons/ui/plus-white.svg" />
                        </template>
                        Добавить авто
                    </UISimpleButton>
                </router-link>
            </div>

            <div
                class="content-header__background"
                :class="{
                    'content-header__background--3-lines-filters': isAdmin(),
                    'content-header__background--2-lines-filters': !isAdmin(),
                    'cars-list-header__background--admin': isAdmin(),
                    'cars-list-header__background--user': !isAdmin(),
                }"
            ></div>
        </div>

        <FilterPanel
            :columns="4"
            @search="onSearch"
            @reset="onReset"
        >
            <UIInput
                id="vin"
                v-model="carsFilter.vin"
                label="VIN"
            />

            <UISelect
                v-if="isAdmin()"
                id="city_id"
                v-model="carsFilterCityId"
                :options="cityOptions"
                label="Город"
                multiple
                searchable
            />

            <UISelect
                v-if="isAdmin()"
                id="dealer_id"
                v-model="carsFilterDealerId"
                :options="dealerOptions"
                label="Дилер"
                multiple
                searchable
            />

            <UISelect
                id="status"
                v-model="carsFilterStatus"
                :options="statusOptions"
                label="Статус"
                multiple
            />

            <UISelect
                id="brand_id"
                v-model="carsFilterBrandId"
                :options="brandOptions"
                label="Марка"
            />

            <UISelect
                id="model_id"
                v-model="carsFilterModelId"
                :options="modelOptions"
                label="Модель"
                multiple
                searchable
            />

            <UISelect
                id="admission_type"
                v-model="carsFilterAdmissionType"
                :options="admissionTypeOptions"
                label="Тип поступления"
            />

            <UISelect
                id="nds_type"
                v-model="carsFilterNdsType"
                :options="ndsTypeOptions"
                label="Тип НДС"
                multiple
            />

            <UIDatePicker
                id="from_sale_date"
                v-model="carsFilter.from_sale_date"
                label="Дата продажи от"
                :options="{ enableTime: false, dateFormat: 'd.m.Y' }"
            />

            <UIDatePicker
                id="to_sale_date"
                v-model="carsFilter.to_sale_date"
                label="Дата продажи до"
                :options="{ enableTime: false, dateFormat: 'd.m.Y' }"
            />

            <UISelect
                v-if="!isAdmin()"
                id="transmission_type"
                v-model="carsFilter.transmission_type"
                :options="transmissionTypOptions"
                label="Тип трансмиссии"
                multiple
            />

            <UIRange
                v-if="!isAdmin()"
                v-model="carsFilter.production_year"
                :min="CarMinProductionYear"
                :max="CarMaxProductionYear"
                :interval="1"
                label="Год выпуска"
                :format-numbers="false"
            />

            <UIRange
                v-model="carsFilter.mileage"
                :min="0"
                :max="999999"
                :interval="100"
                label="Пробег"
                unit="км"
                :format-numbers="true"
            />

            <UIRange
                v-model="carsFilter.start_price"
                :min="0"
                :max="12000000"
                :interval="100000"
                label="Цена"
                unit="₽"
                :format-numbers="true"
            />
        </FilterPanel>

        <UISearchResults
            :count="carsPaginationMeta?.pagination.total || 0"
            :initial-sort="selectedSort"
            :sort-options="sortOptions"
            @update:sort="onSortUpdate"
        />

        <UILoader v-if="isCarsLoading" class="listable" />

        <div v-else class="content__body">
            <div class="cars-list__content">
                <CarListItem
                    v-for="(car, idx) in arCars || []"
                    :key="'car.' + (car?.id ?? idx)"
                    :car="car"
                    @updated="onCarUpdated"
                />
            </div>
        </div>

        <div class="content__footer">
            <TablePagination
                :total-pages="carsPaginationMeta?.pagination?.last_page ?? 1"
                :current-page="carsPaginationMeta?.pagination?.current_page ?? 1"
                :items-per-page="carsPaginationMeta?.pagination?.per_page ?? 10"
                @page-change="changePage"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, onMounted, ref } from 'vue';

    import { SelectOption, SortOption } from '@/types/form.type.ts';
    import {
        CarAdmissionType,
        CarNdsType,
        CarsRequestFilter,
        CarStatus,
        getCarAdmissionTypeSelectOptions,
        getCarNdsTypeSelectOptions,
        getCarStatusSelectOptions,
        CarMinProductionYear,
        CarMaxProductionYear,
        CarMaxMileage,
        CarMaxPrice,
        getCarTransmissionTypeSelectOptions,
    } from '@/types/car.type.ts';
    import { City } from '@/types/city.type.ts';
    import { DealerListItem } from '@/types/dealer.type.ts';
    import { Model } from '@/types/model.type.ts';

    import useCarRepository from '@/repositories/car/useCarRepository.ts';
    import useCityRepository from '@/repositories/city/useCityRepository.ts';
    import useDealerRepository from '@/repositories/dealer/useDealerRepository.ts';
    import useModelRepository from '@/repositories/model/useModelRepository.ts';
    import usePermission from '@/repositories/auth/usePermission.ts';
    import { useCarsFilterStore } from '@/stores/cars/cars-filter.ts';

    import { getBrandSelectOptions } from '@/utils/SelectOptionFillers.ts';
    import { formatToUTC } from '@/utils/DateTimeFormatter.ts';

    import UIDatePicker from '@/components/ui/UIDatePicker/UIDatePicker.vue';
    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import UISearchResults from '@/components/ui/UISearchResults/UISearchResults.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';
    import UIRange from '@/components/ui/UIRange/UIRange.vue';

    import FilterPanel from '@/components/shared/FilterPanel/FilterPanel.vue';
    import TablePagination from '@/components/shared/TablePagination/TablePagination.vue';

    import CarListItem from '@/views/cars/CarsList/components/CarListItem/CarListItem.vue';

    const { arCars, carsPaginationMeta, isCarsLoading, getCars } = useCarRepository();
    const { getAllCities } = useCityRepository();
    const { getAllActiveModels } = useModelRepository();
    const { getAllActiveDealers } = useDealerRepository();
    const { canAddCar, isAdmin } = usePermission();
    const carsFilterStore = useCarsFilterStore();

    const carsFilter = ref<CarsRequestFilter>({ ...carsFilterStore.$state });
    const cityOptions = ref<SelectOption[]>([]);
    const dealerOptions = ref<SelectOption[]>([]);
    const statusOptions = ref<SelectOption[]>(getCarStatusSelectOptions());
    const brandOptions = ref<SelectOption[]>(getBrandSelectOptions());
    const admissionTypeOptions = ref<SelectOption[]>(getCarAdmissionTypeSelectOptions());
    const ndsTypeOptions = ref<SelectOption[]>(getCarNdsTypeSelectOptions());
    const transmissionTypOptions = ref<SelectOption[]>(getCarTransmissionTypeSelectOptions());
    const selectedSort = ref<string>('created_at_desc');
    const sortOptions = ref<SortOption[]>([
        { label: 'по дате добавления (1-9)', value: 'created_at_asc' },
        { label: 'по дате добавления (9-1)', value: 'created_at_desc' },
        { label: 'по цене (возрастание)', value: 'start_price_asc' },
        { label: 'по цене (убывание)', value: 'start_price_desc' },
        { label: 'по дате продажи (1-9)', value: 'sale_date_asc' },
        { label: 'по дате продажи (9-1)', value: 'sale_date_desc' },
    ]);

    const arModels = ref<Model[]>([]);

    const carsFilterCityId = computed({
        get() {
            return carsFilter.value.city_id === undefined || carsFilter.value.city_id.length === 0
                ? undefined
                : carsFilter.value.city_id;
        },
        set(cityId?: number[]) {
            carsFilter.value.city_id = cityId === undefined ? [] : cityId;
        },
    });

    const carsFilterDealerId = computed({
        get() {
            return carsFilter.value.dealer_id === undefined || carsFilter.value.dealer_id.length === 0
                ? undefined
                : carsFilter.value.dealer_id;
        },
        set(dealerId?: number[]) {
            carsFilter.value.dealer_id = dealerId === undefined ? [] : dealerId;
        },
    });

    const carsFilterStatus = computed({
        get() {
            return carsFilter.value.status === undefined || carsFilter.value.status.length === 0
                ? undefined
                : carsFilter.value.status;
        },
        set(status?: CarStatus[]) {
            carsFilter.value.status = status === undefined ? [] : status;
        },
    });

    const carsFilterBrandId = computed({
        get() {
            return carsFilter.value.brand_id === undefined || carsFilter.value.brand_id.length === 0
                ? undefined
                : carsFilter.value.brand_id[0];
        },
        set(brandId?: number) {
            carsFilter.value.brand_id = brandId === undefined ? [] : [brandId];
        },
    });

    const carsFilterModelId = computed({
        get() {
            return carsFilter.value.model_id === undefined || carsFilter.value.model_id.length === 0
                ? undefined
                : carsFilter.value.model_id;
        },
        set(modelId?: number[]) {
            carsFilter.value.model_id = modelId === undefined ? [] : modelId;
        },
    });

    const carsFilterAdmissionType = computed({
        get() {
            return carsFilter.value.admission_type === undefined || carsFilter.value.admission_type.length === 0
                ? undefined
                : carsFilter.value.admission_type[0];
        },
        set(admissionType?: CarAdmissionType) {
            carsFilter.value.admission_type = admissionType === undefined ? [] : [admissionType];
        },
    });

    const carsFilterNdsType = computed({
        get() {
            return carsFilter.value.nds_type === undefined || carsFilter.value.nds_type.length === 0
                ? undefined
                : carsFilter.value.nds_type;
        },
        set(ndsType?: CarNdsType[]) {
            carsFilter.value.nds_type = ndsType === undefined ? [] : ndsType;
        },
    });

    const modelOptions = computed<SelectOption[]>(() => {
        return arModels.value
            .filter((model: Model) => {
                if (carsFilter.value.brand_id === undefined) {
                    return true;
                }

                if (carsFilter.value.brand_id.length === 0) {
                    return true;
                }

                return carsFilter.value.brand_id.includes(model.brand_id);
            })
            .map((model: Model) => ({
                label: model.name,
                value: model.id,
            }));
    });

    function onSearch(): void {
        carsFilter.value.page = 1;
        fetchCars();
    }

    function onReset(): void {
        carsFilter.value = {
            vin: '',
            city_id: [],
            dealer_id: [],
            status: [],
            brand_id: [],
            model_id: [],
            admission_type: [],
            nds_type: [],
            transmission_type: [],
            from_sale_date: '',
            to_sale_date: '',
            production_year: [CarMinProductionYear, CarMaxProductionYear],
            mileage: [0, CarMaxMileage],
            start_price: [0, CarMaxPrice],
            is_confirmation_required: undefined,
            is_active: undefined,
            page: 1,
            sort: carsFilter.value.sort,
        };
        fetchCars();
    }

    function changePage(page: number): void {
        carsFilter.value.page = page;
        fetchCars();
    }

    function onSortUpdate(sortValue: string): void {
        selectedSort.value = sortValue;

        switch (sortValue) {
            case 'created_at_asc':
                carsFilter.value.sort = [{ created_at: 'asc' }];
                break;
            case 'created_at_desc':
                carsFilter.value.sort = [{ created_at: 'desc' }];
                break;
            case 'start_price_asc':
                carsFilter.value.sort = [{ start_price: 'asc' }];
                break;
            case 'start_price_desc':
                carsFilter.value.sort = [{ start_price: 'desc' }];
                break;
            case 'sale_date_asc':
                carsFilter.value.sort = [{ sale_date: 'asc' }];
                break;
            case 'sale_date_desc':
                carsFilter.value.sort = [{ sale_date: 'desc' }];
                break;
        }

        fetchCars();
    }

    function onCarUpdated(): void {
        fetchCars();
    }

    function mapCarsFilterToRequestFilter(): CarsRequestFilter {
        const requestFilter: CarsRequestFilter = {
            production_year: carsFilter.value.production_year,
            mileage: carsFilter.value.mileage,
            start_price: carsFilter.value.start_price,
            page: carsFilter.value.page,
            sort: carsFilter.value.sort,
        };

        if (carsFilter.value.vin) {
            requestFilter.vin = carsFilter.value.vin;
        }

        if (carsFilter.value.city_id && carsFilter.value.city_id.length > 0) {
            requestFilter.city_id = carsFilter.value.city_id;
        }

        if (carsFilter.value.dealer_id && carsFilter.value.dealer_id.length > 0) {
            requestFilter.dealer_id = carsFilter.value.dealer_id;
        }

        if (carsFilter.value.status && carsFilter.value.status.length > 0) {
            requestFilter.status = carsFilter.value.status;
        }

        if (carsFilter.value.brand_id && carsFilter.value.brand_id.length > 0) {
            requestFilter.brand_id = carsFilter.value.brand_id;
        }

        if (carsFilter.value.model_id && carsFilter.value.model_id.length > 0) {
            requestFilter.model_id = carsFilter.value.model_id;
        }

        if (carsFilter.value.admission_type && carsFilter.value.admission_type.length > 0) {
            requestFilter.admission_type = carsFilter.value.admission_type;
        }

        if (carsFilter.value.nds_type && carsFilter.value.nds_type.length > 0) {
            requestFilter.nds_type = carsFilter.value.nds_type;
        }

        if (carsFilter.value.transmission_type && carsFilter.value.transmission_type.length > 0) {
            requestFilter.transmission_type = carsFilter.value.transmission_type;
        }

        if (carsFilter.value.from_sale_date) {
            requestFilter.from_sale_date = formatToUTC(carsFilter.value.from_sale_date, {
                fromFormat: 'dd.LL.yyyy',
                toFormat: 'yyyy-LL-dd',
                withTime: false,
            });
        }

        if (carsFilter.value.to_sale_date) {
            requestFilter.to_sale_date = formatToUTC(carsFilter.value.to_sale_date, {
                fromFormat: 'dd.LL.yyyy',
                toFormat: 'yyyy-LL-dd',
                withTime: false,
            });
        }

        if (carsFilter.value.is_confirmation_required !== undefined) {
            requestFilter.is_confirmation_required = carsFilter.value.is_confirmation_required;
        }

        if (carsFilter.value.is_active !== undefined) {
            requestFilter.is_active = carsFilter.value.is_active;
        }

        return requestFilter;
    }

    function fetchFormFilterOptions(): void {
        if (isAdmin()) {
            getAllCities().then((cities: City[]) => {
                if (cities) {
                    cityOptions.value = cities.map((city: City) => ({
                        label: city.name,
                        value: city.id,
                    }));
                }
            });

            getAllActiveDealers().then((dealers: DealerListItem[]) => {
                if (dealers) {
                    dealerOptions.value = dealers.map((dealer: DealerListItem) => ({
                        label: dealer.name + ' (' + dealer.sap_code + ')',
                        value: dealer.id,
                    }));
                }
            });
        }

        getAllActiveModels().then((models: Model[]) => {
            arModels.value = models;
        });
    }

    function fetchCars(): void {
        carsFilterStore.$patch({ ...carsFilter.value });
        getCars(mapCarsFilterToRequestFilter());
    }

    onMounted((): void => {
        fetchCars();
        fetchFormFilterOptions();
    });
</script>

<style scoped lang="scss">
    @use './CarsList.scss' as *;
</style>
